<template>
  <div>
    <PageHeader>
      <div class="row ml-3">
        <div class="ml-1" v-if="isSuperAdmin">
          <div class="badgebutton active" @click="createBoard()">
            <i class="far fa-plus"></i>
            Create Board
          </div>
        </div>

        <div class="ml-3">
          <div class="badgebutton active" @click="createSpecialOccurrence()">
            <i class="far fa-plus"></i>
            Create Special Occurrence
          </div>
        </div>
      </div>

      <template v-slot:right>
        <router-link class="mt-2 mb-2" :to="{ name: routes.specialOccurrence.overview }">
          <div class="btn btn-primary c-white btn-sm mr-1">
            <i class="fa fa-history"></i>
            Special Occurrence Archive
          </div>
        </router-link>

        <user-time-zone></user-time-zone>

        <router-link
          tag="div"
          :to="{ name: routes.notificationsettings }"
          class="badgebutton info active text-nowrap ml-2"
          v-b-tooltip
          title="User Settings"
        >
          <i class="far fa-user"></i>
        </router-link>

        <router-link
          tag="div"
          :to="{ name: isAdmin ? routes.labels.generalLabels.name : routes.generalSettings.materialRequest.name }"
          class="badgebutton info active text-nowrap ml-2"
          v-b-tooltip
          title="General Settings"
          v-if="isAdmin || isMaterialRequestAdmin"
        >
          <i class="far fa-cogs"></i>
        </router-link>
      </template>
    </PageHeader>

    <div class="container">
      <!-- Boards -->
      <div>
        <div class="item-title mt-3">Boards</div>

        <div class="row" v-if="availableBoards.length">
          <div class="col-3" v-for="board in availableBoards" :key="board.id">
            <router-link class="navigation-card navigation-card-board mt-3 mb-2" tag="a" :to="{ name: 'Board', params: { id: board.id } }">
              <div class="navigation-card-title">
                <div>
                  {{ board.title }}
                  <span v-if="board.enableShifts">
                    <i class="far fa-clock"></i>
                  </span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
        <div v-else>
          <div class="d-flex align-items-center justify-content-center mt-4">
            <div class="no-access-error">No boards available</div>
          </div>
        </div>
      </div>

      <!-- Special Occurrences -->
      <div>
        <div class="item-title mt-4 d-flex justify-content-between align-items-center">
          <span>Active Special Occurrences</span>
        </div>

        <div class="text-center" v-if="occurrencesLoading">
          <loading-spinner></loading-spinner>
        </div>

        <div v-else>
          <div class="row pl-4 pr-4" v-if="occurrences.length">
            <table class="os-table table table-striped table-hover mt-3 bg-white border" v-if="occurrences.length">
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">Type</th>
                  <th scope="col">Aircraft</th>
                  <th scope="col">Name</th>
                  <th scope="col">Link</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="so in occurrences" :key="so.id" @click.stop="navigateToOccurrence(so, so.id)" class="cursor-pointer">
                  <td class="os-table-type container-with-required-warn" scope="col">
                    <div class="d-flex">
                      {{ so.type }}
                    </div>
                    <i
                      class="far fa-info-circle required-warn"
                      v-if="!so.isOccurrenceFulFilled"
                      :title="publicConstants.notAllMandatoryFieldsFilled"
                      v-b-tooltip:hover
                    />
                  </td>
                  <td>
                    <div v-if="so.technicalOccurrenceType" :class="so.technicalOccurrenceType.cssClass" class="badge badgebutton">
                      {{so.technicalOccurrenceType?.name}}
                    </div>
                  </td>
                  <td>
                    <div v-if="so.aircraftRegistration" class="badge badgebutton active">
                      {{so.aircraftRegistration}}
                    </div>
                  </td>
                  <th class="os-table-name" scope="col">{{ so.name }}</th>
                  <th class="os-table-name" scope="col">
                    <button class="badgebutton active" @click.stop="navigateToOccurrence(so, so.id)">Open</button>
                    <button
                      v-if="so.linkedMaterialRequestId"
                      class="badgebutton active ml-2"
                      @click.stop="navigateToMaterialRequest(so.linkedMaterialRequestId)"
                    >
                      Material Request
                    </button>
                  </th>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else>
            <div class="d-flex align-items-center justify-content-center mt-4">
              <div class="no-access-error">There are currently no Special Occurrences</div>
            </div>
          </div>
        </div>
      </div>

      <AppInfo />
    </div>
    <b-modal ref="newBoard" id="newBoard" body-class="modal-body-wo" hide-footer hide-header size="lg" title="Add new Board">
      <add-board></add-board>
    </b-modal>

    <b-modal
      ref="newSpecialOccurrence"
      id="newSpecialOccurrence"
      body-class="modal-body-wo"
      hide-footer
      hide-header
      size="l"
      title="Add new Special Occurrence"
    >
      <add-special-occurrence></add-special-occurrence>
    </b-modal>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import TaskCard from '@/components/TaskCard.vue';
  import TaskDetail from '@/components/TaskDetail/TaskDetail.vue';
  import Component from 'vue-class-component';
  import { BoardModule, BoardStore } from '@/store/board';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import PageHeader from '@/components/PageHeader.vue';
  import AddBoard from '@/components/AddBoard.vue';
  import AddSpecialOccurrence from '@/components/AddSpecialOccurrence.vue';
  import RouteNames from '@/types/routeNames';
  import { UserModule, UserStore } from '@/store/user';
  import UserTimeZone from '@/components/User/UserTimeZone.vue';
  import { SpecialOccurrenceDto, SpecialOccurrenceListDto } from "@/apiclient/apiclient_generated";
  import { specialOccurrenceClient } from '@/apiclient/opsboardapi';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
  import SpecialOccurrenceService from '@/services/specialOccurrence.service';
  import PublicConstants from '@/types/publicConstants';

  @Component({
    components: {
      LoadingSpinner,
      UserTimeZone,
      AddBoard,
      AddSpecialOccurrence,
      PageHeader,
      TaskCard,
      TaskDetail,
    },
  })
  export default class BoardsComponent extends Vue {
    routes = RouteNames;
    private boardStore: BoardModule = BoardStore;
    private occurrenceStore: SpecialOccurrenceModule = SpecialOccurrenceStore;
    private users: UserModule = UserStore;
    occurrences: SpecialOccurrenceListDto[] = [];
    occurrencesLoading = true;
    publicConstants = PublicConstants;

    async mounted() {
      await this.boardStore.loadAllBoards();
      await this.boardStore.loadAvailableBoards();
      await this.occurrenceStore.getAll();

      this.occurrences = await specialOccurrenceClient.getAllGrouped();
      this.occurrencesLoading = false;
    }

    get isAdmin() {
      return this.users.user && this.users.user.isAdmin;
    }

    get isMaterialRequestAdmin() {
      return this.users.user && this.users.user.isMaterialRequestAdmin;
    }

    get isSuperAdmin() {
      return this.users.user && this.users.user.isSuperAdmin;
    }

    get availableBoards() {
      return this.lodash.orderBy(this.boardStore.availableBoards, (t) => t.title);
    }

    get specialOccurrences() {
      return this.lodash.orderBy(this.occurrenceStore.allOccurrences, 'createdDate', 'desc');
    }

    navigateToOccurrence(so: SpecialOccurrenceListDto, id) {
      const route = {
        name: SpecialOccurrenceService.getTechnicalOccurrenceRouteName(so),
        params: { occId: id },
      };

      this.$router.push(route);
    }

    navigateToMaterialRequest(id) {
      const route = {
        name: RouteNames.specialOccurrence.root,
        params: { occId: id },
      };

      this.$router.push(route);
    }

    async createBoard() {
      const modal: any = this.$refs['newBoard'];
      await modal.show();
    }

    async createSpecialOccurrence() {
      const modal: any = this.$refs['newSpecialOccurrence'];
      await modal.show();
    }
  }
</script>

<style lang="scss" scoped>
  .item-title {
    font-size: 1.5rem;
    margin-left: 8px;
    font-weight: bold;
  }

  .navigation-card {
    background-size: cover;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    transition: all 0.2s;
    height: 75px;
    box-shadow: 0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 0 1px rgba(9, 30, 66, 0.08);
    position: relative;

    .navigation-card-type {
      position: absolute;
      top: 10px;
      left: 10px;
      font-size: 11px;
      color: #ccc;
    }

    &:hover {
      box-shadow: 0 8px 16px -2px rgba(9, 30, 66, 0.25), 0 0 0 1px rgba(9, 30, 66, 0.08);
      transform: scale(1.1);
      text-decoration: none;
    }

    .navigation-card-title {
      position: relative;
      font-weight: bold;
      font-size: 1.3rem;
      text-decoration: none;
      display: flex;
      justify-content: center;
      align-items: center;

      .navigation-card-type {
        position: absolute;
        left: 10px;
        top: 10px;
      }
    }

    &.navigation-card-board {
      background: #fefefe;
      color: #333;
    }

    &.navigation-card-specialoccurrence {
      background: #4e667d;
      color: white;
    }
  }
</style>
